/**
 * Workflow steps are the one navigation control that lives in the shared
 * layout, and both the web stepper and the host ribbon press it. Neither may
 * move the user on its own: the step is a destination handed to the navigation
 * guard, and the jump happens only inside the guard's commit.
 */

import { useCallback } from 'react';
import { workflowStepDestination, type NavigationSource } from '../../lib/setupRevision';
import { useUIStore } from '../../stores/useUIStore';
import type { Screen } from '../../types';
import type { WorkflowStepMeta } from '../../config/workflowUi';
import { requestGuardedNavigation } from '../navigation';

export interface WorkflowStepNavigationInput {
  steps: readonly WorkflowStepMeta[];
  currentScreen: Screen;
  /** Set while the current screen refuses to be left at all (for example mid-apply). */
  blocked: boolean;
}

export type WorkflowStepNavigationHandler = (
  stepScreen: Screen,
  stepIndex: number,
  source?: NavigationSource
) => void;

export const useWorkflowStepNavigation = ({
  steps,
  currentScreen,
  blocked,
}: WorkflowStepNavigationInput): WorkflowStepNavigationHandler => {
  const jumpTo = useUIStore((state) => state.jumpTo);

  return useCallback(
    (stepScreen: Screen, stepIndex: number, source: NavigationSource = 'workflowStep') => {
      if (blocked || stepScreen === currentScreen) {
        return;
      }

      const nextHistory = steps.slice(0, stepIndex + 1).map((step) => step.screen);
      const label = steps[stepIndex]?.label;

      void requestGuardedNavigation(workflowStepDestination(source, stepScreen, label), () => {
        jumpTo(stepScreen, nextHistory);
      });
    },
    [blocked, currentScreen, jumpTo, steps]
  );
};
